import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { modeFavicon } from '../../mode-nevek';
import { ProfilMenuComponent } from '../profil-menu/profil-menu.component';
import { Szinmod, keretVilagos } from '../../szinmod';
import { FelhasznaloBeallitasService } from '../../felhasznalo-beallitas.service';

/** Egy menüpont a sávban (a bal menü `appPages`-éből). */
interface SavPont { cim: string; url: string; ikon: string; kulso: boolean; tiltott: boolean; }
interface SavBlokk { fejlec: string; pontok: SavPont[]; }
interface SavSzekcio { kulcs: string; cim: string; ikon: string; blokkok: SavBlokk[]; db: number; }

/*  A SZEKCIÓK – ugyanaz a beosztás, mint a bal menü tervében (Napi munka, Osztályom…). A menü
 *  pontjai az adatbázisból jönnek (menuszerkesztő), itt csak az útvonaluk dönti el, melyik
 *  szekcióba kerülnek; az ismeretlen a „Továbbiak” alá. */
const SZEKCIOK: { kulcs: string; cim: string; ikon: string }[] = [
  { kulcs: 'napi', cim: 'Napi munka', ikon: 'today-outline' },
  { kulcs: 'osztaly', cim: 'Osztályom', ikon: 'people-outline' },
  { kulcs: 'tanugy', cim: 'Tanügy', ikon: 'school-outline' },
  { kulcs: 'kommunikacio', cim: 'Kommunikáció', ikon: 'chatbubbles-outline' },
  { kulcs: 'dokumentumok', cim: 'Dokumentumok', ikon: 'document-text-outline' },
  { kulcs: 'gazdasag', cim: 'Gazdaság', ikon: 'wallet-outline' },
  { kulcs: 'sajat', cim: 'Saját ügyeim', ikon: 'person-outline' },
  { kulcs: 'segitseg', cim: 'Segítség', ikon: 'help-circle-outline' },
  { kulcs: 'egyeb', cim: 'Továbbiak', ikon: 'ellipsis-horizontal-circle-outline' },
];

const SZEKCIO_UT: Record<string, string> = {
  '/home': 'napi', '/faliujsag': 'napi', '/naptar': 'napi', '0/suli-naplo': 'napi', '/nyilvantarto': 'napi',
  '/osztalyfonok': 'osztaly', '/ertekelesek/4': 'osztaly', '/fogadoora': 'osztaly', '/szuloi-jegyek': 'osztaly',
  '/szuloi-orarend': 'osztaly',
  '0/ttf': 'tanugy', '0/suli-adatok': 'tanugy', '/tanmenet': 'tanugy', '/kozossegi-szolgalat': 'tanugy',
  '/tkt': 'tanugy', '/tanterem': 'tanugy',
  '/message': 'kommunikacio', '/ertekezlet': 'kommunikacio', '/kerveny': 'kommunikacio', '/feladataim': 'kommunikacio',
  '/dokumentumaim': 'dokumentumok', '/admin/dokumentumok': 'dokumentumok', '/kreta-sajat': 'dokumentumok',
  '/kreta-diff': 'dokumentumok',
  '/penzugy': 'gazdasag', '0/suli-gazdasag': 'gazdasag', '/etkezes': 'gazdasag', '/etlap': 'gazdasag', '/konyveles': 'gazdasag',
  '/berszamfejtes': 'gazdasag',
  '/eszkozeim': 'sajat', '/laptop': 'sajat', '/berlet': 'sajat', '/csaladi-belepes': 'sajat',
  '/license': 'segitseg', '/changelog': 'segitseg', '/hibajegy': 'segitseg', '/adatjavitas': 'segitseg',
};

function szekcioUtbol(url: string): string {
  const u = String(url || '');
  if (SZEKCIO_UT[u]) return SZEKCIO_UT[u];
  if (/tudasbazis|support\./i.test(u)) return 'segitseg';
  return 'egyeb';
}

/**
 *  SULINFORM – FÓKUSZ-SÁV (2026-10-16; élesben is választható, alapból Klasszikus: modul-tema.ts › FOKUSZ_KEPES).
 *
 *  A SULINFORM oldalai önálló útvonalak saját fejléccel, ezért a Fókusz-sáv egy közös sáv az oldal
 *  fölött (a NAPLO/BÉR/ESZKÖZ fejlécének párja): a modul ikonja, „Ugrás…” kereső (Ctrl+K) a bal menü
 *  pontjaira (ugyanaz a jogosultság-szűrt `appPages`), tanévválasztó, profil-lenyíló. A bal sáv a
 *  SULINFORM Fókuszában MARAD (a user kérése); a szekció-lenyílók (`szekciokkal`) csak bal menü
 *  nélküli keretben jelennének meg, keskeny képernyőn egy „Menü” panellel.
 */
@Component({
  selector: 'app-sulinform-fokusz-sav',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, ProfilMenuComponent],
  templateUrl: './sulinform-fokusz-sav.component.html',
  styleUrls: ['./sulinform-fokusz-sav.component.scss'],
})
export class SulinformFokuszSavComponent implements OnInit, OnChanges, OnDestroy {
  @Input() oldalak: any[] = [];
  @Input() olvasatlan = 0;
  @Input() tanevek: any[] = [];
  @Input() tanevNev = '';
  /** Saját szekció-lenyílók (bal menü nélküli keretben). SULINFORM-ban a bal menü navigál
   *  (a user kérése), ezért alapból nincsenek – a sávon a jel, a kereső, a tanév és a profil marad. */
  @Input() szekciokkal = false;
  @Output() tanevValt = new EventEmitter<any>();

  @ViewChild('ugrasMezo') ugrasMezo?: ElementRef<HTMLInputElement>;

  readonly ikon = modeFavicon('SULINFORM');
  szekciok: SavSzekcio[] = [];
  nyitott = '';
  mobilNyitva = false;
  aktivSzekcio = '';
  aktivUrl = '';

  q = '';
  talalatok: (SavPont & { szekcio: string })[] = [];
  kijelolt = 0;

  private sub?: Subscription;

  /** A menü és a fejléc színe: sötét / világos / a rendszer szerint (böngészőnként) – szinmod.ts. */
  get szinmod(): Szinmod { return this.fb.adat.szinmod || 'rendszer'; }

  constructor(private router: Router, private el: ElementRef<HTMLElement>, private fb: FelhasznaloBeallitasService) {}

  get keretVilagos(): boolean { return keretVilagos(); }

  szinmodValt(m: string) {
    const uj: Szinmod = (m === 'sotet' || m === 'vilagos') ? m : 'rendszer';
    //  A felhasználó beállításaiba (kiszolgálón is) – ugyanaz, mint a Beállítások oldalon.
    this.fb.ment({ szinmod: uj === 'rendszer' ? null : uj }).catch(() => { /* helyben él */ });
  }

  ngOnInit() {
    this.aktivAllit(this.router.url);
    this.sub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) { this.aktivAllit(e.urlAfterRedirects); this.nyitott = ''; this.mobilNyitva = false; }
    });
  }
  ngOnChanges() { this.epit(); this.aktivAllit(this.router.url); }
  ngOnDestroy() { this.sub?.unsubscribe(); }

  private pont(p: any): SavPont {
    return { cim: String(p?.title || ''), url: String(p?.url || ''), ikon: p?.icon ? `${p.icon}-outline` : 'ellipse-outline',
      kulso: !!p?.external || /^https?:\/\//i.test(String(p?.url || '')), tiltott: !!p?.disabled };
  }

  private epit() {
    const m = new Map<string, SavBlokk[]>();
    for (const p of this.oldalak || []) {
      const url = String(p?.url || '');
      if (!url) continue;
      const k = szekcioUtbol(url);
      if (!m.has(k)) m.set(k, []);
      const blokkok = m.get(k)!;
      if (url[0] === '0') {
        const al = (p.submenu || []).map((s: any) => this.pont(s)).filter((s: SavPont) => s.url);
        if (al.length) blokkok.push({ fejlec: String(p.title || ''), pontok: al });
      } else {
        let sima = blokkok.find((b) => !b.fejlec);
        if (!sima) { sima = { fejlec: '', pontok: [] }; blokkok.unshift(sima); }
        sima.pontok.push(this.pont(p));
      }
    }
    this.szekciok = SZEKCIOK
      .filter((s) => m.has(s.kulcs))
      .map((s) => {
        const blokkok = m.get(s.kulcs)!;
        return { ...s, blokkok, db: blokkok.reduce((n, b) => n + b.pontok.length, 0) };
      });
  }

  private aktivAllit(url: string) {
    const u = String(url || '').split('?')[0];
    this.aktivUrl = u;
    this.aktivSzekcio = this.szekciok.find((s) => s.blokkok.some((b) => b.pontok.some((p) => !p.kulso && p.url === u)))?.kulcs || '';
  }

  valt(kulcs: string, ev: Event) {
    ev.stopPropagation();
    this.nyitott = this.nyitott === kulcs ? '' : kulcs;
  }

  nyit(p: SavPont) {
    if (p.tiltott) return;
    this.nyitott = ''; this.mobilNyitva = false; this.q = ''; this.talalatok = [];
    if (p.kulso) { window.open(p.url, '_blank', 'noopener'); return; }
    this.router.navigateByUrl(p.url);
  }

  kezdolap() { this.nyit({ cim: 'Kezdőlap', url: '/home', ikon: '', kulso: false, tiltott: false }); }

  vanJelzes(s: SavSzekcio): boolean {
    return s.kulcs === 'kommunikacio' && this.olvasatlan > 0;
  }

  tanevValasztas(id: any) {
    const t = (this.tanevek || []).find((x: any) => String(x?.id) === String(id));
    if (t) this.tanevValt.emit(t);
  }
  get aktivTanevId(): string {
    return String((this.tanevek || []).find((t: any) => t?.name === this.tanevNev)?.id ?? '');
  }

  // ── Ugrás… ────────────────────────────────────────────────────────────────────────────────
  keres() {
    const norm = (s: string) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const szavak = norm(this.q).split(/\s+/).filter(Boolean);
    this.kijelolt = 0;
    if (!szavak.length) { this.talalatok = []; return; }
    const ki: (SavPont & { szekcio: string })[] = [];
    for (const s of this.szekciok) for (const b of s.blokkok) for (const p of b.pontok) {
      const h = norm(`${p.cim} ${b.fejlec} ${s.cim}`);
      if (szavak.every((sz) => h.includes(sz))) ki.push({ ...p, szekcio: b.fejlec ? `${s.cim} › ${b.fejlec}` : s.cim });
    }
    this.talalatok = ki.slice(0, 10);
  }
  ugrasBillentyu(ev: KeyboardEvent) {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); this.kijelolt = Math.min(this.kijelolt + 1, this.talalatok.length - 1); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); this.kijelolt = Math.max(this.kijelolt - 1, 0); }
    else if (ev.key === 'Enter') { const p = this.talalatok[this.kijelolt]; if (p) this.nyit(p); }
    else if (ev.key === 'Escape') { this.q = ''; this.talalatok = []; }
  }

  @HostListener('document:keydown', ['$event'])
  gyorsbillentyu(ev: KeyboardEvent) {
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'k' || ev.key === 'K') && !this.router.url.startsWith('/admin')) {
      ev.preventDefault();
      this.ugrasMezo?.nativeElement.focus();
    }
  }

  /** A lenyíló bezárul, ha mellé kattintunk. */
  @HostListener('document:click', ['$event'])
  kattintas(ev: MouseEvent) {
    if (!this.el.nativeElement.contains(ev.target as Node)) { this.nyitott = ''; this.talalatok = this.q ? this.talalatok : []; }
  }
}
